import React from 'react'
import { useSelector } from 'react-redux'
import { Base64 } from 'js-base64'
import PropTypes from 'prop-types'
import { styled } from '@mui/material/styles'
import { Table, TableBody, TableRow, TableCell, Grid, Typography, Box } from '@mui/material'
import {
  formatAddress,
  convertShippingInfoToCityStateZip,
  dateTimeFormatter,
  timeFormatter,
  dateFormatter,
} from 'utils/data-manipulation/valueFormatter'
import { Caption } from '../StyledElements'

const TableValueCell = styled(TableCell)(() => ({
  borderBottom: 'none',
  paddingTop: 0,
  paddingBottom: 0,
  paddingRight: 0,
}))

const TableLabelCell = styled(TableCell)(() => ({
  verticalAlign: 'top',
  borderBottom: 'none',
  padding: 0,
  fontWeight: 600,
}))

function ReceiptGridMetaInfo({
  customer,
  ticketCreator,
  carrierName,
  effectiveDate,
  paymentDate,
  transportationInfo,
  dispatchTicket,
  notes,
  type,
  paymentMethod,
  checkNumber,
  inboundTicketAddress,
  licenseNumber,
  plateNumber,
}) {
  const { defaultYardTimezone } = useSelector(state => state.auth)
  const { showUnitOnReceipts, enablePrintedDocCompliance, useMinimalObt } = useSelector(
    state => state.featureFlags?.variables || {},
  )
  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { includeOperatorLastName } = settings?.documentSettings?.general || {}

  return useMinimalObt ? (
    <>
      <Grid item sx={{ border: '1px solid black', padding: 1 }} xs={12}>
        <Grid item>
          <strong>Customer</strong>
          <Typography variant="body2">{customer?.customerCommonIdentifierString || '---'}</Typography>
          <Typography variant="body2">{`${formatAddress(inboundTicketAddress, false, true)}`}</Typography>
          <Typography variant="body2">{`${convertShippingInfoToCityStateZip(inboundTicketAddress)}`}</Typography>
        </Grid>
      </Grid>
      <Grid
        sx={{
          flex: 1,
          border: '1px solid black',
          ...(useMinimalObt ? { borderTop: '0px' } : { borderLeft: '0px' }),
          padding: 1,
        }}
        pl={2}
      >
        <strong>Details</strong>
        <Grid item container>
          <Grid item xs={4} sm={3} mb={2}>
            <Caption sx={{ fontSize: '12px' }}>Carrier</Caption>
            <Typography variant="body2">{carrierName || transportationInfo?.carrierName || ''}</Typography>
          </Grid>
          <Grid item xs={4} sm={3} mb={2}>
            <Caption sx={{ fontSize: '12px' }}>Driver Name</Caption>
            <Typography variant="body2">{transportationInfo.driverName || ''}</Typography>
          </Grid>
          <Grid item xs={4} sm={3} mb={2}>
            <Caption sx={{ fontSize: '12px' }}>Truck #:</Caption>
            <Typography variant="body2">{transportationInfo.carrierNumber || ''}</Typography>
          </Grid>
          <Grid item xs={4} sm={3} mb={2}>
            <Caption sx={{ fontSize: '12px' }}>Trailer #:</Caption>
            <Typography variant="body2">{transportationInfo.trailerNumber || ''}</Typography>
          </Grid>
          <Grid item xs={4} sm={3} mb={2}>
            <Caption sx={{ fontSize: '12px' }}>Time of Arrival:</Caption>
            <Typography variant="body2">
              {effectiveDate ? timeFormatter(effectiveDate, defaultYardTimezone) : ''}
            </Typography>
            <Typography variant="body2">
              {effectiveDate ? dateFormatter(effectiveDate, defaultYardTimezone) : ''}
            </Typography>
          </Grid>
          <Grid item xs={4} sm={3} mb={2}>
            <Caption sx={{ fontSize: '12px' }}>Time of Payment:</Caption>
            <Typography variant="body2">
              {paymentDate ? timeFormatter(effectiveDate, defaultYardTimezone) : '---'}
            </Typography>
            <Typography variant="body2">
              {paymentDate ? dateFormatter(effectiveDate, defaultYardTimezone) : '---'}
            </Typography>
          </Grid>
          {type === 'PRINT_PURCHASE_TICKET' && (
            <Grid item xs={4} sm={3} mb={2}>
              <Caption sx={{ fontSize: '12px' }}>Payment Type:</Caption>
              <Typography variant="body2">{paymentMethod || '---'}</Typography>
            </Grid>
          )}
          {checkNumber && (
            <Grid item xs={4} sm={3} mb={2}>
              <Caption sx={{ fontSize: '12px' }}>Check #:</Caption>
              <Typography variant="body2">{checkNumber}</Typography>
            </Grid>
          )}
          {notes && (
            <Grid item xs={9}>
              <Caption>Notes</Caption>
              {notes.map(note => (
                <Typography key={Base64.encode(note?.name)} variant="body2">
                  {note?.name}
                </Typography>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  ) : (
    <>
      <Grid display="flex">
        <Grid>
          <Table
            size="small"
            sx={{
              td: {
                fontSize: '12px',
              },
            }}
          >
            <TableBody>
              <TableRow>
                <TableLabelCell>Operator:</TableLabelCell>
                <TableValueCell>{`${ticketCreator.firstName}${includeOperatorLastName ? ` ${ticketCreator.lastName}` : ''}`}</TableValueCell>
              </TableRow>
              <TableRow>
                <TableLabelCell>Time of Arrival:</TableLabelCell>
                <TableValueCell>
                  {effectiveDate
                    ? `${dateTimeFormatter(effectiveDate, { reversed: true, timeZone: defaultYardTimezone })}`
                    : ' '}
                </TableValueCell>
              </TableRow>
              {type === 'PRINT_PURCHASE_TICKET' ? (
                <TableRow>
                  <TableLabelCell>PAID:</TableLabelCell>
                  <TableValueCell>
                    {paymentDate
                      ? `${dateTimeFormatter(paymentDate, { reversed: true, timeZone: defaultYardTimezone })}`
                      : '---'}
                  </TableValueCell>
                </TableRow>
              ) : null}
              <TableRow>
                <TableLabelCell>Customer:</TableLabelCell>
                <TableValueCell sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                  {customer?.customerCommonIdentifierString || '---'}
                </TableValueCell>
              </TableRow>
              <TableRow>
                <TableLabelCell>Address:</TableLabelCell>
                <TableValueCell sx={{ whiteSpace: 'pre-line' }}>
                  <>
                    {`${formatAddress(inboundTicketAddress, false, true)}`}
                    <br />
                    {`${convertShippingInfoToCityStateZip(inboundTicketAddress)}`}
                  </>
                </TableValueCell>
              </TableRow>
              {type === 'PRINT_PURCHASE_TICKET' ? (
                <>
                  <TableRow>
                    <TableLabelCell>License Plate:</TableLabelCell>
                    <TableValueCell>{plateNumber || '---'}</TableValueCell>
                  </TableRow>
                  <TableRow>
                    <TableLabelCell>Drivers License #:</TableLabelCell>
                    <TableValueCell>{licenseNumber || '---'}</TableValueCell>
                  </TableRow>
                </>
              ) : null}
              {dispatchTicket && (
                <TableRow>
                  <TableLabelCell>Dispatch Ticket #:</TableLabelCell>
                  <TableValueCell>{dispatchTicket || '---'}</TableValueCell>
                </TableRow>
              )}
              {(carrierName || transportationInfo?.carrierName) && (
                <TableRow>
                  <TableLabelCell>Carrier:</TableLabelCell>
                  <TableValueCell>{carrierName || transportationInfo?.carrierName}</TableValueCell>
                </TableRow>
              )}
              {transportationInfo?.carrierNumber && (
                <TableRow>
                  <TableLabelCell>Carrier #:</TableLabelCell>
                  <TableValueCell>{transportationInfo.carrierNumber}</TableValueCell>
                </TableRow>
              )}
              {transportationInfo?.containerNumber && (
                <TableRow>
                  <TableLabelCell>Container #:</TableLabelCell>
                  <TableValueCell>{transportationInfo.containerNumber}</TableValueCell>
                </TableRow>
              )}
              {transportationInfo?.billOfLading && (
                <TableRow>
                  <TableLabelCell>BOL #:</TableLabelCell>
                  <TableValueCell>{transportationInfo.billOfLading}</TableValueCell>
                </TableRow>
              )}
              {type === 'PRINT_PURCHASE_TICKET' && (
                <TableRow>
                  <TableLabelCell>Payment Type:</TableLabelCell>
                  <TableValueCell>{paymentMethod || '---'}</TableValueCell>
                </TableRow>
              )}
              {checkNumber && (
                <TableRow>
                  <TableLabelCell>Check #:</TableLabelCell>
                  <TableValueCell>{checkNumber}</TableValueCell>
                </TableRow>
              )}
              {showUnitOnReceipts && (
                <TableRow>
                  <TableLabelCell>Unit of Measure:</TableLabelCell>
                  <TableValueCell>{defaultYardUm}</TableValueCell>
                </TableRow>
              )}
              {enablePrintedDocCompliance && (
                <>
                  <TableRow>
                    <TableLabelCell>Gross Weighmaster:</TableLabelCell>
                    <TableValueCell>{`${ticketCreator.firstName}${includeOperatorLastName ? ` ${ticketCreator.lastName}` : ''}`}</TableValueCell>
                  </TableRow>
                  <TableRow>
                    <TableLabelCell>Tare Weighmaster:</TableLabelCell>
                    <TableValueCell>{`${ticketCreator.firstName}${includeOperatorLastName ? ` ${ticketCreator.lastName}` : ''}`}</TableValueCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </Grid>
      </Grid>
      <Box mt={3}>
        <Typography variant="caption" color="textSecondary">
          NOTES
        </Typography>
        {notes?.map(note => (
          <Typography variant="body2">{note?.name}</Typography>
        ))}
      </Box>
    </>
  )
}

ReceiptGridMetaInfo.propTypes = {
  customer: PropTypes.object.isRequired,
  ticketCreator: PropTypes.object.isRequired,
  carrierName: PropTypes.string,
  effectiveDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  transportationInfo: PropTypes.object.isRequired,
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
    }),
  ),
  type: PropTypes.string.isRequired,
  dispatchTicket: PropTypes.string,
  paymentMethod: PropTypes.string,
  checkNumber: PropTypes.string,
  inboundTicketAddress: PropTypes.object,
  paymentDate: PropTypes.string,
  licenseNumber: PropTypes.string,
  plateNumber: PropTypes.string,
}

ReceiptGridMetaInfo.defaultProps = {
  notes: [],
  dispatchTicket: '',
  carrierName: '',
  paymentMethod: '',
  checkNumber: '',
  inboundTicketAddress: {},
  paymentDate: '',
  licenseNumber: '',
  plateNumber: '',
}

export default ReceiptGridMetaInfo
